<template>
  <div class="packages-overview">

    <Header v-if="showHeader"
            :amount="amount"
            :headerImage="headerImage"
            :name="name"
            />

    <main v-if="filters && siteId"
          class="packages-overview__container"
    >

      <Filters v-model:activeFilters="activeFilters"
               :amount="amount"
               :filters="filters"
               :loadingResults="loadingResults"
               :siteId="siteId"
               v-model:visible="filtersVisible"
               @setFiltersLoaded="setFiltersLoaded"
               />

      <Results v-model:activeFilters="activeFilters"
               v-model:amount="amount"
               :amountPerPage="amountPerPage"
               :filters="filters"
               :filtersLoaded="filtersLoaded"
               v-model:loading="loadingResults"
               v-model:page="page"
               v-model:pageLoaded="pageLoaded"
               :siteId="siteId"
               v-model:sortOrder="sortOrder"
               @openFilters="openFilters"
               @resetFilters="resetFilters"
               />

    </main>

  </div>
</template>

<script>
import _ from 'lodash';
import api from '@/api';
import { SEARCH_PARAMS_PAGE } from '@/constants';

import Header from '@/components/Header.vue';
import Filters from '@/components/Filters.vue';
import Results from '@/components/Results.vue';

export default {
  name: 'App',

  components: {
    Header,
    Filters,
    Results,
  },

  data: () => ({
    activeFilters: {},
    amount: null,
    amountPerPage: 10,
    filters: null,
    filtersLoaded: false,
    filtersVisible: false,
    headerImage: null,
    loadingResults: false,
    name: null,
    page: null,
    pageLoaded: false,
    siteId: null,
    sortOrder: 'default',
  }),

  computed: {
    showHeader() {
      return (
        this.headerImage !== null
        && this.name !== null
      );
    },
  },

  watch: {
    activeFilters: {
      deep: true,
      handler() {
        this.updateUrl();
      },
    },

    page(__, oldValue) {
      this.updateUrl();

      if (oldValue !== null) {
        this.scrollToTop();
      }
    },
  },

  async created() {
    await this.initAppData();
  },

  methods: {
    initAppData() {
      const { host, pathname } = window.location;

      return api.getAppData(host, pathname)
        .then((response) => {
          const {
            filters,
            headerImage,
            siteId,
            title,
          } = response.data;

          this.filters = filters;
          this.headerImage = headerImage;
          this.name = title;
          this.siteId = siteId;
        })
        .catch((error) => {
          console.error(error);
        });
    },

    openFilters() {
      this.filtersVisible = true;
    },

    updateUrl() {
      if (!this.filtersLoaded || !this.pageLoaded) {
        return;
      }

      const { host, pathname, protocol } = window.location;
      const url = new URL(`${protocol}//${host}${pathname}`);

      if (this.page > 1) {
        url.searchParams.append(SEARCH_PARAMS_PAGE, this.page);
      }

      Object.keys(this.activeFilters).forEach((index) => {
        const value = this.activeFilters[index];

        if (Array.isArray(value)) {
          value.forEach((arrayValue) => {
            url.searchParams.append(index, arrayValue);
          });
        } else if (value instanceof Date) {
          const year = value.getFullYear();
          const month = (value.getMonth() + 1).toString().padStart(2, '0');
          const day = value.getDate().toString().padStart(2, '0');
          url.searchParams.append(index, `${year}-${month}-${day}`);
        } else if (!_.isObject(value) && value !== undefined) {
          url.searchParams.append(index, value);
        }
      });

      window.history.replaceState(null, null, url);
    },

    resetFilters() {
      this.activeFilters = {};
    },

    scrollToTop() {
      this.$el.scrollIntoView({
        behavior: 'smooth',
      });
    },

    setFiltersLoaded(filtersLoaded) {
      this.filtersLoaded = filtersLoaded;
    },
  },
};
</script>

<style lang="scss">
  @import '../../assets/css/helpers/colors';
  @import '../../assets/css/helpers/variables';
  @import '../../assets/css/helpers/mixins/breakpoints';
  @import '@/assets/css/animations.scss';

  .packages-overview {
    --max-width: 1200px;
    --spacing-m: 15px;
    --spacing-l: 25px;
    --sidebar-width: 0px;
    --sidebar-spacing: 0px;

    --color-grey-light: #{$grey-light};
    --color-grey: #{$grey};
    --color-primary: #{$color-primary};
    --color-secondary: #{$color-secondary};
    --color-divider: #{$color-divider};

    --font-secondary: #{$font-family-sec};

    @include laptop-and-larger {
      --sidebar-width: 280px;
      --sidebar-spacing: var(--spacing-l);
    }

    &__container {

      @include laptop-and-larger {
        display: flex;
        max-width: var(--max-width);
        padding: 0 var(--spacing-m);
        margin: 0 auto;
      }
    }
  }
</style>
